import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';

import * as dateFns from 'date-fns';
import _, { isEmpty } from 'lodash';
import {
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useAuthContext } from 'src/auth/hooks';
import { saveBudgetPatches } from 'src/components/plaid/budget';
import { timestamp } from 'src/utils/firebase';
import { findChangedObjects } from 'src/utils/findChangedObjects';
import { currencyFormatter } from 'src/utils/currency-formatter';
import { PatchCategoryRow } from './PatchCategoryRow';
import { useFinanceContext } from '../datasource';

export default memo(PatchBudget);

function PatchBudget({ budgets, column, row, open, onClose, initialForecast }) {
  const { user } = useAuthContext();
  const { accounts: allAccounts, assets, liabilities } = useFinanceContext();
  const [patches, setPatches] = useState([]);

  const nameForAccount = (id) =>
    [...allAccounts, ...(assets?.allAssets ?? []), ...(liabilities?.allLiabilities ?? [])]?.find(
      (e) => e?.id === id
    );

  useEffect(() => {
    if (!open) {
      // Reset patches when the dialog is closed
      setPatches([]);
      return;
    }

    // Create a deep clone of the budgets with patches
    const flattenedPatches = _.cloneDeep(budgets?.flatMap((b) => b?.patches)) ?? [];

    // Check if the new category is included in any existing patches
    const categoryExists = flattenedPatches?.some((patch) =>
      Object.keys(patch?.categories ?? {}).includes(row)
    );

    if (!categoryExists) {
      // If the category doesn't exist in any patches, create a new one
      const newPatch = {
        user_id: user?.uid,
        timestamp: timestamp(initialForecast?.date),
        categories: { [row]: {} /* parseFloat(initialForecast?.value) */ },
      };
      flattenedPatches.push(newPatch);
    }

    // Set the patches state with the updated value
    setPatches(flattenedPatches.filter(Boolean));
  }, [open, budgets, initialForecast, row, user?.uid]);

  const [loading, setLoading] = useState(false);
  const handleSave = () => {
    setLoading(true);
    saveBudgetPatches(patches)
      .then(() => {
        setLoading(false);
        onClose();
      })
      .catch((e) => {
        setLoading(false);
      });
  };

  const handleChange = useCallback(
    (patchId, newData) => {
      // find the patch to update either based on ID, or existing ones by date if it's not for a scenario
      const patchToUpdate = patches?.find((p) => p?.id === patchId);

      if (patchToUpdate) {
        const updatedPatch = {
          user_id: user.id,
          timestamp: timestamp(initialForecast?.date),
          ...patchToUpdate,
          categories: { ...patchToUpdate?.categories, ...newData },
        };

        if (JSON.stringify(patchToUpdate) === JSON.stringify(updatedPatch)) {
          console.log('No changes detected, skipping update');
          return;
        }

        setPatches((prevPatches) => [
          ...(prevPatches?.filter((e) => e?.id !== patchId) ?? []),
          updatedPatch,
        ]);
      } else {
        if (!initialForecast?.date) {
          console.warn('No date passed to new budget');
          return;
        }
        const newPatch = {
          user_id: user.id,
          timestamp: timestamp(initialForecast?.date),
          categories: { ...newData },
        };
        console.log('Creating new patch', newPatch);
        setPatches((prevPatches) => [...prevPatches, newPatch]);
      }
    },
    [user?.id, initialForecast?.date, patches]
  );

  const onDeleteRow = (patchId, cat) => {
    // Find the index of the patch with the matching patchId in your state
    const patchIndex = patches.findIndex((patch) => patch.id === patchId);

    if (patchIndex !== -1) {
      // Clone the original patch to avoid mutating the state directly
      const updatedPatch = { ...patches[patchIndex] };

      // Remove the specified category from the patch's categories
      if (updatedPatch.categories) {
        delete updatedPatch.categories[cat];
      }

      // Create a new array with the updated patch at the same index
      const updatedPatches = [
        ...patches.slice(0, patchIndex),
        updatedPatch,
        ...patches.slice(patchIndex + 1),
      ];

      // Update the state with the new patches array
      setPatches(updatedPatches);
    }
  };

  const isDefaultScenario = (patch) => _.isEmpty(patch?.scenario);
  const isScenario = (patch) => !isDefaultScenario(patch);

  const orderedPatches = useMemo(() => {
    // Group patches by ID first
    const patchesById = [...patches].reduce((acc, e) => {
      const patch = { ...e };
      if (patch?.categories?.[row]) {
        const { id } = patch;
        if (!acc[id]) {
          acc[id] = patch;
        } else {
          // Merge categories for same ID
          acc[id].categories[row] = {
            ...acc[id].categories[row],
            ...patch.categories[row],
          };
        }
      }
      return acc;
    }, {});

    // Convert back to array and use existing sorting logic
    const results = Object.values(patchesById);

    results.sort((a, b) => {
      const aDisabled = isScenario(a);
      const bDisabled = isScenario(b);
      // Compare timestamps
      const timestampDiff =
        (a.timestamp?.toDate()?.getTime() ?? 0) - (b.timestamp?.toDate()?.getTime() ?? 0);

      // If timestamps are equal, handle disabled items
      if (timestampDiff === 0) {
        // If one item is disabled and the other is not, place the disabled one after the enabled one
        if (aDisabled && !bDisabled) {
          return -1;
        }
        if (!aDisabled && bDisabled) {
          return 1;
        }
      }

      // Otherwise, maintain the sorting based on timestamps
      return timestampDiff;
    });

    results.reverse();
    return results;
  }, [patches]);

  const canSave = React.useMemo(() => {
    const before = budgets?.flatMap((b) => b?.patches ?? {});
    const after = patches;
    const changes = findChangedObjects(before, after);

    const newPatch = patches.find((e) => !e.id) || after.find((e) => !e.id);
    const isChanged = changes.length > 0 || before?.length < after?.length;

    const incomplete =
      changes?.some((e) => Object.values(e?.categories ?? {}).some(isEmpty)) ||
      Object.values(newPatch?.categories ?? {}).some((e) => isEmpty(e));

    return isChanged && !incomplete;
  }, [budgets, patches]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{column ?? ''} Budget</DialogTitle>
      <DialogContent>
        <Stack spacing={1} padding={2}>
          {orderedPatches.length === 0 && <Alert severity="info">No budget changes</Alert>}
          {orderedPatches.map((patch, index) => (
            <PatchItemRow
              disabled={isScenario(patch)}
              key={patch.id ?? index}
              patch={patch}
              filterKey={row}
              initialForecast={initialForecast}
              onChange={handleChange}
              onDeleteRow={onDeleteRow}
              nameForAccount={nameForAccount}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          size="large"
          fullWidth
          color="success"
          onClick={handleSave}
          disabled={!canSave}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export const PatchItemRow = memo(
  ({ patch, initialForecast, onChange, onDeleteRow, nameForAccount, disabled, filterKey }) => {
    const date = initialForecast?.date ?? patch?.timestamp?.toDate();
    const segmentName = date ? dateFns.format(date, 'MMM yy') : 'no date';

    const categories = useMemo(() => patch?.categories, [patch?.categories]);

    const handleChange = useCallback(
      (category, value) => {
        if (JSON.stringify(patch.categories[category]) !== JSON.stringify(value)) {
          onChange(patch?.id, { [category]: value });
        }
      },
      [patch, onChange]
    );

    const rows = useMemo(
      () =>
        Object.entries(categories ?? {})
          .filter(([category]) => (filterKey && category === filterKey) || !filterKey)
          .map(([category, data]) => (
            <PatchCategoryRow
              key={`${category}-${segmentName}`}
              id={`${category}-${segmentName}`}
              category={category}
              placeholder={currencyFormatter
                .format(Math.abs(initialForecast?.value))
                .replace('$', '')}
              value={data}
              onChange={handleChange}
              onDelete={() => onDeleteRow(patch?.id, category)}
              nameForAccount={nameForAccount}
              disabled={disabled}
            />
          )),
      [
        categories,
        segmentName,
        initialForecast,
        handleChange,
        onDeleteRow,
        nameForAccount,
        disabled,
      ]
    );

    return (
      <>
        <span>
          <strong>{segmentName}</strong>
        </span>
        {rows}
      </>
    );
  },
  (prevProps, nextProps) =>
    JSON.stringify(prevProps.patch) === JSON.stringify(nextProps.patch) &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.initialForecast === nextProps.initialForecast
);
